import { History, X, Trash2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * SearchHistory component displays recent AI searches
 * @param {Object} props
 * @param {Array} props.history - Array of search history items
 * @param {Function} props.onSelect - Callback when a search is selected
 * @param {Function} props.onRemove - Callback to remove a single search
 * @param {Function} props.onClear - Callback to clear all history
 * @param {string} props.variant - 'compact' for modal, 'full' for results page
 * @param {string} props.className - Additional CSS classes
 */
export function SearchHistory({ 
  history = [], 
  onSelect, 
  onRemove, 
  onClear,
  variant = 'compact',
  className 
}) {
  if (!history || history.length === 0) {
    return null;
  }

  // Format relative time
  const formatTime = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  };

  // Compact variant for modal
  if (variant === 'compact') {
    return (
      <div className={cn("mt-3", className)}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-xs text-sky-700/70">
            <History className="w-3 h-3" />
            <span>Recent searches</span>
          </div>
          {history.length > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); onClear?.(); }}
              className="text-xs text-sky-600/60 hover:text-sky-700 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {history.slice(0, 5).map((item, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); onSelect?.(item.query); }}
              className="group inline-flex items-center gap-1 px-2.5 py-1 bg-white/70 hover:bg-white rounded-full text-xs text-sky-800 border border-sky-200/50 hover:border-sky-300 transition-all shadow-sm"
            >
              <Clock className="w-2.5 h-2.5 text-sky-500/70" />
              <span className="max-w-[120px] truncate">{item.query}</span>
              <X 
                className="w-3 h-3 text-sky-400 opacity-0 group-hover:opacity-100 hover:text-sky-600 transition-opacity"
                onClick={(e) => { e.stopPropagation(); onRemove?.(item.query); }}
              />
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Full variant for results page
  return (
    <div className={cn("bg-muted/30 rounded-lg p-4 border", className)}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <History className="w-4 h-4 text-muted-foreground" />
          <span>Recent Searches</span>
          <span className="text-xs text-muted-foreground font-normal">({history.length})</span>
        </div>
        {history.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="h-7 text-xs text-muted-foreground hover:text-destructive gap-1"
          >
            <Trash2 className="w-3 h-3" />
            Clear all
          </Button>
        )}
      </div>
      <div className="space-y-1.5">
        {history.map((item, index) => (
          <div
            key={index}
            className="group flex items-center justify-between p-2.5 bg-background rounded-md border hover:border-sky-200 hover:bg-sky-50/50 cursor-pointer transition-all"
            onClick={() => onSelect?.(item.query)}
            data-testid={`search-history-item-${index}`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm text-foreground truncate">{item.query}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{formatTime(item.timestamp)}</span>
              <button
                onClick={(e) => { e.stopPropagation(); onRemove?.(item.query); }}
                className="p-1 rounded hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove search"
              >
                <X className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
